import React, { createContext, useContext, useState } from 'react';
import { SortingState } from '@tanstack/react-table';
import { usePost } from '../API';
import { logAdminPanelError } from '../utils/adminPanel.utils';

export type ApplicantStatus = 'inReview' | 'accepted' | 'rejected' | 'fraud' | null;

export type Applicant = {
  uid: string;
  name: string;
  organization: string;
  source: string;
  hidden?: boolean;
  created_at?: number;
  status?: ApplicantStatus;
  [key: string]: any; // Additional fields from applicantinfo
};

export type ApplicantSubsurvey = {
  subsurveyNameTranslations: Record<string, string>;
  url?: string;
  send_link?: any;
  part: string;
};

export type ApplicantsQueryParams = {
  page: number;
  limit: number;
  search: string;
  sorting: SortingState;
};

export type ApplicantsData = {
  applicants: Applicant[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type SubsurveysData = {
  subsurveys: ApplicantSubsurvey[];
  foundDisplayInfo?: Record<string, string>;
  error?: string;
};

export type ApplicantStatsData = {
  hasConfiguration: boolean;
  total?: number;
  inReview?: number;
  accepted?: number;
  rejected?: number;
  fraud?: number;
};

type ApplicantsContextType = {
  // Query params state
  queryParams: ApplicantsQueryParams;
  setQueryParams: React.Dispatch<React.SetStateAction<ApplicantsQueryParams>>;

  // Applicants data and loading state
  applicantsData: ApplicantsData | null;
  isApplicantsLoading: boolean;
  applicantsError: string | null;
  fetchApplicants: (params: ApplicantsQueryParams) => Promise<void>;

  // Stats data
  statsData: ApplicantStatsData | null;
  isStatsLoading: boolean;
  statsError: string | null;
  fetchApplicantStats: () => Promise<void>;

  // Subsurveys data
  subsurveysByUid: Record<string, SubsurveysData>;
  subsurveyLoadingByUid: Record<string, boolean>;
  subsurveysErrors: Record<string, string>;

  fetchApplicantSubsurveys: (uid: string) => Promise<void>;

  logApplicantCreation: () => Promise<boolean>;

  // Export data
  fetchApplicantsForExport: () => Promise<Applicant[]>;
  logApplicantExport: (format: string, applicantCount: number) => Promise<void>;
};

type ApplicantsProviderProps = {
  children: React.ReactNode;
};

const ApplicantsContext = createContext<ApplicantsContextType | undefined>(undefined);

export const useApplicantsContext = () => {
  const context = useContext(ApplicantsContext);
  if (!context) {
    throw new Error("useApplicantsContext must be used within an ApplicantsProvider");
  }
  return context;
};

export const ApplicantsProvider: React.FC<ApplicantsProviderProps> = ({ children }) => {
  const [queryParams, setQueryParams] = useState<ApplicantsQueryParams>({
    page: 1,
    limit: 25,
    search: '',
    sorting: []
  });

  const [applicantsData, setApplicantsData] = useState<ApplicantsData | null>(null);
  const [isApplicantsLoading, setIsApplicantsLoading] = useState(false);
  const [applicantsError, setApplicantsError] = useState<string | null>(null);

  const [statsData, setStatsData] = useState<ApplicantStatsData | null>(null);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [subsurveysByUid, setSubsurveysByUid] = useState<Record<string, SubsurveysData>>({});
  const [subsurveyLoadingByUid, setSubsurveyLoadingByUid] = useState<Record<string, boolean>>({});
  const [subsurveysErrors, setSubsurveysErrors] = useState<Record<string, string>>({});

  const getAdminPanelApplicants = usePost("/api/admin_panel/applicants");
  const getApplicantStats = usePost("/api/admin_panel/applicants/stats");
  const getApplicantSubsurveys = usePost("/api/admin_panel/applicants/:uid/subsurveys");
  const logApplicantCreationPost = usePost("/api/admin_panel/applicants/log_creation");
  const logAudit = usePost("/api/admin_panel/log_audit_entry");

  const fetchApplicants = async (params: ApplicantsQueryParams) => {
    setIsApplicantsLoading(true);
    setApplicantsError(null);

    const apiParams: any = { pagination: { page: params.page, limit: params.limit } };

    if (params.search && params.search.trim()) {
      apiParams.search = params.search.trim();
    }

    if (params.sorting && params.sorting.length > 0) {
      apiParams.pagination.sorting = params.sorting.map(sort => ({
        sortBy: sort.id,
        sortOrder: sort.desc ? 'desc' as const : 'asc' as const
      }));
    }

    const result = await getAdminPanelApplicants(apiParams);
    setIsApplicantsLoading(false);

    if (result.success && result.data) {
      setApplicantsData({
        applicants: result.data.data as Applicant[],
        total: result.data.total as number,
        page: result.data.page as number,
        limit: result.data.limit as number,
        totalPages: result.data.totalPages as number
      });
    } else if (result.error) {
      const errorMessage = result.message || 'Failed to fetch applicants';
      setApplicantsError(errorMessage);
      logAdminPanelError(result.error, result.message);
    } else {
      const errorMessage = 'Failed to fetch applicants';
      setApplicantsError(errorMessage);
    }
  };

  const fetchApplicantSubsurveys = async (uid: string) => {
    setSubsurveyLoadingByUid(prev => ({ ...prev, [uid]: true }));
    setSubsurveysErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[uid];
      return newErrors;
    });

    const result = await getApplicantSubsurveys({ uid });

    setSubsurveyLoadingByUid(prev => ({ ...prev, [uid]: false }));

    if (result.success && result.data) {
      setSubsurveysByUid(prev => ({
        ...prev,
        [uid]: {
          subsurveys: (result.data.subsurveys || []) as ApplicantSubsurvey[],
          foundDisplayInfo: result.data.foundDisplayInfo as Record<string, string> | undefined,
          error: result.data.error as string | undefined
        }
      }));
    } else if (result.error) {
      const errorMessage = result.message || 'Failed to fetch subsurveys';
      setSubsurveysErrors(prev => ({ ...prev, [uid]: errorMessage }));
      logAdminPanelError(result.error, result.message);
    } else {
      const errorMessage = 'Failed to fetch subsurveys';
      setSubsurveysErrors(prev => ({ ...prev, [uid]: errorMessage }));
    }
  };

  const fetchApplicantStats = async () => {
    setIsStatsLoading(true);
    setStatsError(null);

    const result = await getApplicantStats({});
    setIsStatsLoading(false);

    if (result.success && result.data) {
      setStatsData({
        hasConfiguration: result.data.hasConfiguration as boolean,
        total: result.data.total as number | undefined,
        inReview: result.data.inReview as number | undefined,
        accepted: result.data.accepted as number | undefined,
        rejected: result.data.rejected as number | undefined,
        fraud: result.data.fraud as number | undefined
      });
    } else if (result.error) {
      const errorMessage = result.message || 'Failed to fetch applicant statistics';
      setStatsError(errorMessage);
      logAdminPanelError(result.error, result.message);
    } else {
      const errorMessage = 'Failed to fetch applicant statistics';
      setStatsError(errorMessage);
    }
  };

  const logApplicantCreation = async () => {
    const result = await logApplicantCreationPost({})

    if (result.error) {
      logAdminPanelError(result.error, result.message)
      return false
    }

    return true
  }

  const fetchApplicantsForExport = async (): Promise<Applicant[]> => {
    const apiParams: any = {
      pagination: {
        page: 1,
        limit: 500 // Large limit to get all applicants
      }
    };

    // Include search filter if active
    if (queryParams.search && queryParams.search.trim()) {
      apiParams.search = queryParams.search.trim();
    }

    // Include sorting if active
    if (queryParams.sorting && queryParams.sorting.length > 0) {
      apiParams.pagination.sorting = queryParams.sorting.map(sort => ({
        sortBy: sort.id,
        sortOrder: sort.desc ? 'desc' as const : 'asc' as const
      }));
    }

    const result = await getAdminPanelApplicants(apiParams);

    if (result.success && result.data) {
      return result.data.data as Applicant[];
    }

    throw new Error('Failed to fetch applicants for export');
  };

  const logApplicantExport = async (format: string, applicantCount: number): Promise<void> => {
    try {
      await logAudit({
        eventName: "APPLICANTS_EXPORTED",
        value: {
          format: format,
          applicantCount: applicantCount,
          searchQuery: queryParams.search || undefined,
        }
      });
    } catch (error) {
      console.error('Failed to log applicant export:', error);
    }
  };

  const value = {
    queryParams,
    setQueryParams,
    applicantsData,
    isApplicantsLoading,
    applicantsError,
    fetchApplicants,
    statsData,
    isStatsLoading,
    statsError,
    fetchApplicantStats,
    subsurveysByUid,
    subsurveyLoadingByUid,
    subsurveysErrors,
    fetchApplicantSubsurveys,
    logApplicantCreation,
    fetchApplicantsForExport,
    logApplicantExport,
  };

  return (
    <ApplicantsContext.Provider value={value}>
      {children}
    </ApplicantsContext.Provider>
  );
};
